<script setup>
import { computed, watch, ref } from 'vue'
import { gApp } from '../State.js'
import { getColorFromUsername } from '../SharedUtils'
import { FreeTrialConfig, License } from '../Common/License.js'
import SignedInView from './SignedInView.vue'

const user = computed(() => gApp.getUser())
const license = computed(() => {
  return gApp.getLicense();
})
const isAdmin = computed(() => gApp.isAdminUser())

async function signOutUser() {
  await gApp.signOutUser();
}

function openUpgradeLink() {
  window.open('https://www.heatwise-hvac.com/pricing', '_blank');
}

function openBillingPortal() {
  window.open('https://polar.sh/heatwise-hvac/portal', '_blank');
}


// List of test licenses
const testLicenses = [
  { id: 'freeTrial', name: 'Free Trial', createFn: () => License.createFreeTrial() },
  { id: 'indefiniteFreeTrial', name: 'Indefinite Free Trial', createFn: () => License.createIndefiniteFreeTrial() },
  { id: 'freeTrialAlmostDone', name: 'Trial (2 days left)', createFn: () => License.createFreeTrialAlmostDone() },
  { id: 'freeTrialExpired', name: 'Expired Trial', createFn: () => License.createFreeTrialExpired() },
  { id: 'commercial', name: 'Commercial', createFn: () => License.createPaidLicense({ planName: 'Commercial', quantity: 1 }) },
  { id: 'residential', name: 'Residential', createFn: () => License.createPaidLicense({ planName: 'Residential', quantity: 1 }) },
  { id: 'paidTeam', name: 'Team License (5 seats)', createFn: () => License.createTeamLicense(5, user.value?.email) },
  { id: 'paidExpired', name: 'Expired Paid', createFn: () => License.createExpiredLicense(true) },
  { id: 'cancelled', name: 'Cancelled', createFn: () => License.createCancelledLicense() },
  { id: 'oneTimeCommercial', name: 'One-Time Commercial', createFn: () => License.createOneTimePurchaseLicense({ planName: 'Commercial' }) },
  { id: 'oneTimeResidential', name: 'One-Time Residential', createFn: () => License.createOneTimePurchaseLicense({ planName: 'Residential' }) },
  { id: 'oneTimeExpired', name: 'One-Time Expired', createFn: () => License.createExpiredOneTimePurchaseLicense() },
];

// User impersonation
const impersonateToken = ref('')
const impersonateLoading = ref(false)
const impersonateError = ref('')

// Admin functions for testing licenses
async function setLicenseType(licenseType) {
  const licenseConfig = testLicenses.find(l => l.id === licenseType);
  
  if (licenseConfig) {
    const newLicense = licenseConfig.createFn();
    newLicense.debugOverride = true;
    gApp.license.value = newLicense;
    console.log('License updated:', newLicense);
  }
}

// Admin function to impersonate a user
async function impersonateUser() {
  if (!impersonateToken.value.trim()) {
    impersonateError.value = 'Custom token is required';
    return;
  }
  
  impersonateLoading.value = true;
  impersonateError.value = '';
  
  try {
    await gApp.signInWithCustomToken(impersonateToken.value.trim());
    console.log(`Successfully signed in with custom token`);
    
    // Clear the form
    impersonateToken.value = '';
  } catch (error) {
    console.error('Error signing in with custom token:', error);
    impersonateError.value = error.message || 'Failed to sign in with custom token';
  } finally {
    impersonateLoading.value = false;
  }
}

</script>

<template>
  <SignedInView class="UserProfileContainer">
    <div class="HeaderContainer">
      <h1 class="PageH1">Account Settings</h1>
      <button class="CreateBtn ReturnBtn" @click="gApp.router.push('/')">
        <i class="bi-house-door mr-xxs"></i>Return to Projects
      </button>
    </div>
    
    <div v-if="user" class="ProfileContent">
      <div class="ProfileSection">
        <h2 class="SectionTitle">Account Information</h2>
        <div class="ProfileCard">
          <div class="ProfileHeader">
            <div class="ProfileAvatar" :style="{ backgroundColor: getColorFromUsername(user.email) }">
              {{ user.email ? user.email[0].toUpperCase() : ' ' }}
            </div>
            <div class="ProfileDetails">
              <p class="ProfileEmail">{{ user.email }}</p>
              <p class="ProfileMember">Member since {{ new Date(user.creationTime).toLocaleDateString() }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="ProfileSection">
        <h2 class="SectionTitle">License Information</h2>
        <div class="ProfileCard">
          <div class="LicenseInfo">
            <div class="LicenseHeader">
              <div class="LicenseIcon" :class="{ 'PaidLicense': license && license.isPaid() }">
                <i :class="license && license.isPaid() ? 'bi-award-fill' : 'bi-hourglass-split'"></i>
              </div>
              <div class="LicenseDetails">
                <p class="LicenseType">{{ license ? license.getLicenseName() : 'No license information' }}</p>
                <p class="LicenseStatus">{{ license ? license.getLicenseStatus() : '' }}</p>
              </div>
            </div>
            
            <div v-if="license && license.isPaid()" class="LicenseMetadata">
              <div class="LicenseMetaItem">
                <p class="LicenseMetaLabel">Status</p>
                <p class="LicenseMetaValue" :class="{ 'ActiveStatus': license.isActive(), 'InactiveStatus': !license.isActive() }">
                  {{ license.status }}
                </p>
              </div>
              
              <div v-if="license.expiresAt" class="LicenseMetaItem">
                <p class="LicenseMetaLabel">Expires</p>
                <p class="LicenseMetaValue">{{ new Date(license.expiresAt).toLocaleDateString() }}</p>
              </div>
              
              <div v-if="license.renewsAt" class="LicenseMetaItem">
                <p class="LicenseMetaLabel">Renews</p>
                <p class="LicenseMetaValue">{{ new Date(license.renewsAt).toLocaleDateString() }}</p>
              </div>
              
              <div v-if="license.quantity > 1" class="LicenseMetaItem">
                <p class="LicenseMetaLabel">Seats</p>
                <div class="LicenseMetaValue LicenseQuantity">
                  <i class="bi-people-fill mr-xxs"></i>{{ license.quantity }}
                </div>
              </div>
            </div>
            
            <div v-if="license && license.isPaid()" class="BillingActions">
              <button class="ManageBillingBtn" @click="openBillingPortal">
                <i class="bi-credit-card mr-xxs"></i>Manage Billing
              </button>
            </div>
            
            <div v-if="license && !license.isPaid()" class="UpgradeCTA">
              <div v-if="license.expiresAt === FreeTrialConfig.INDEFINITE" class="IndefiniteTrialContainer">
                <div class="IndefiniteTrialIcon">
                  <i class="bi-infinity"></i>
                </div>
                <p class="IndefiniteTrialText">You have unlimited access to HeatWise</p>
              </div>
              <div v-else-if="license.isExpired()" class="ExpiredTrialContainer">
                <div class="ExpiredTrialIcon">
                  <i class="bi-exclamation-triangle-fill"></i>
                </div>
                <p class="ExpiredTrialText">Your free trial has expired</p>
                <p class="UpgradeText">Upgrade to a paid license to continue using HeatWise.</p>
                <button class="CreateBtn UpgradeBtn" @click="openUpgradeLink">
                  <i class="bi-arrow-up-circle mr-xxs"></i>Upgrade Now
                </button>
              </div>
              <div v-else-if="license.getDaysRemaining() >= 0" class="TrialInfoContainer">
                <div class="TrialProgress">
                  <div class="TrialProgressBar" :style="{ width: `${Math.min(100, (license.getDaysRemaining() / FreeTrialConfig.TRIAL_DAYS) * 100)}%` }"></div>
                </div>
                <p class="TrialDaysLeft">{{ license.getDaysRemaining() }} days remaining</p>
                <p class="UpgradeText">Upgrade to a paid license to continue using HeatWise after your trial ends.</p>
                <button class="CreateBtn UpgradeBtn" @click="openUpgradeLink">
                  <i class="bi-arrow-up-circle mr-xxs"></i>Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="ProfileSection">
        <h2 class="SectionTitle">Account Settings</h2>
        <div class="ProfileCard">
          <div class="SettingsOption">
            <button class="TextBtn" @click="signOutUser">
              <i class="bi-box-arrow-right mr-xs"></i>Sign Out
            </button>
          </div>
        </div>
      </div>
      
      <!-- Admin-only license testing section -->
      <div v-if="isAdmin" class="ProfileSection">
        <h2 class="SectionTitle">Admin Tools</h2>
        <div class="ProfileCard">
          <div class="AdminSection">
            <h3 class="AdminSectionTitle">License Testing</h3>
            <p class="help-text mb-s">Set the license to one of the following test types:</p>
            
            <div class="LicenseTestButtons">
              <button 
                v-for="license in testLicenses" 
                :key="license.id"
                class="LicenseTestBtn" 
                @click="setLicenseType(license.id)"
              >
                {{ license.name }}
              </button>
            </div>
            
            <div class="AdminNote mt-s">
              <p class="f-xs"><i class="bi-info-circle mr-xxs"></i> These changes are temporary and will reset when the page is refreshed.</p>
            </div>
          </div>
          
          <div class="AdminSection mt-m">
            <h3 class="AdminSectionTitle">User Impersonation</h3>
            <p class="help-text mb-s">Sign in as a specific user using a custom token:</p>
            
            <div class="ImpersonateForm">
              <div class="FormRow">
                <label class="FormLabel" for="impersonate-token">Custom Token:</label>
                <input 
                  id="impersonate-token"
                  v-model="impersonateToken" 
                  type="text" 
                  class="FormInput"
                  placeholder="Enter custom token"
                  :disabled="impersonateLoading"
                />
              </div>
              
              <div v-if="impersonateError" class="ErrorMessage mb-s">
                <i class="bi-exclamation-triangle mr-xxs"></i>{{ impersonateError }}
              </div>
              
              <button 
                class="ImpersonateBtn" 
                @click="impersonateUser"
                :disabled="impersonateLoading || !impersonateToken.trim()"
              >
                <i v-if="impersonateLoading" class="bi-arrow-clockwise mr-xxs"></i>
                <i v-else class="bi-person-fill-gear mr-xxs"></i>
                {{ impersonateLoading ? 'Signing In...' : 'Sign In With Token' }}
              </button>
            </div>
            
            <div class="AdminNote mt-s">
              <p class="f-xs"><i class="bi-info-circle mr-xxs"></i> This will sign you out of your current session and sign you in with the provided token.</p>
            </div>
          </div>
          
        </div>
      </div>
    </div>
    <div v-else class="NotSignedIn">
      <p>Signing in...</p>
    </div>
  </SignedInView>
</template>

<style scoped>
.UserProfileContainer {
  max-width: 800px;
  margin: 0 auto;
  padding: var(--space-m);
}

.HeaderContainer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-m);
}

.ReturnBtn {
  white-space: nowrap;
}

.LoadingContainer, .ErrorContainer, .NotSignedIn {
  text-align: center;
  padding: var(--space-l);
  background-color: var(--grey-95);
  border-radius: var(--br-m);
  margin-top: var(--space-m);
}

.ErrorMessage {
  color: var(--delete-red);
}

.ProfileSection {
  margin-bottom: var(--space-l);
}

.SectionTitle {
  font-size: var(--f-m);
  color: var(--pc-dark);
  margin-bottom: var(--space-s);
  font-weight: 600;
}

.ProfileCard {
  background-color: white;
  border-radius: var(--br-m);
  padding: var(--space-m);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.ProfileHeader {
  display: flex;
  align-items: center;
  margin-bottom: var(--space-m);
}

.ProfileAvatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--f-xl);
  color: white;
  font-weight: bold;
  margin-right: var(--space-m);
}

.ProfileDetails {
  flex: 1;
}

.ProfileEmail {
  font-size: var(--f-m);
  font-weight: 600;
  margin-bottom: var(--space-xxs);
}

.ProfileMember {
  font-size: var(--f-s);
  color: var(--grey-50);
}

.LicenseQuantity {
  font-size: var(--f-s);
  color: var(--pc-dark);
  font-weight: 500;
  margin-top: var(--space-xxs);
  display: flex;
  align-items: center;
}

.LicenseInfo {
  padding: var(--space-xs) 0;
}

.LicenseHeader {
  display: flex;
  align-items: center;
  margin-bottom: var(--space-s);
}

.LicenseIcon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--f-l);
  margin-right: var(--space-m);
  background-color: var(--grey-90);
  color: var(--grey-40);
}

.LicenseIcon.PaidLicense {
  background-color: rgba(62, 85, 204, 0.1);
  color: var(--pc);
}

.LicenseDetails {
  flex: 1;
}

.LicenseType {
  font-size: var(--f-m);
  font-weight: 600;
  margin-bottom: var(--space-xxs);
  color: var(--pc-dark);
}

.LicenseStatus {
  font-size: var(--f-s);
  color: var(--grey-50);
}

.LicenseMetadata {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--space-m);
  margin-top: var(--space-m);
  padding-top: var(--space-m);
  border-top: 1px solid var(--grey-90);
}

.LicenseMetaItem {
  display: flex;
  flex-direction: column;
}

.LicenseMetaLabel {
  font-size: var(--f-xs);
  color: var(--grey-50);
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: var(--space-xxs);
}

.LicenseMetaValue {
  font-size: var(--f-m);
  font-weight: 500;
}

.ActiveStatus {
  color: #2ecc71;
}

.InactiveStatus {
  color: var(--warning-orange);
}

.UpgradeCTA {
  margin-top: var(--space-m);
  padding: var(--space-m);
  background-color: var(--pc-lightest);
  border-radius: var(--br-m);
  text-align: center;
}

.TrialInfoContainer {
  margin-bottom: var(--space-s);
  padding: var(--space-s);
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: var(--br-s);
  border: 1px solid rgba(62, 85, 204, 0.1);
}

.TrialProgress {
  height: 8px;
  background-color: var(--grey-90);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: var(--space-xxs);
}

.TrialProgressBar {
  height: 100%;
  background-color: var(--pc);
  border-radius: 4px;
}

.TrialDaysLeft {
  font-size: var(--f-s);
  font-weight: 600;
  color: var(--pc-dark);
  text-align: right;
}

.IndefiniteTrialContainer {
  padding: var(--space-m);
  background-color: rgba(46, 204, 113, 0.1);
  border-radius: var(--br-m);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-s);
  border: 1px solid rgba(46, 204, 113, 0.3);
}

.IndefiniteTrialIcon {
  font-size: var(--f-l);
  color: #2ecc71;
}

.IndefiniteTrialText {
  font-size: var(--f-m);
  font-weight: 600;
  color: #2ecc71;
}

.ExpiredTrialContainer {
  padding: var(--space-m);
  background-color: rgba(231, 76, 60, 0.1);
  border-radius: var(--br-m);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-s);
  border: 1px solid rgba(231, 76, 60, 0.3);
  text-align: center;
}

.ExpiredTrialIcon {
  font-size: var(--f-l);
  color: #e74c3c;
  margin-bottom: var(--space-xxs);
}

.ExpiredTrialText {
  font-size: var(--f-m);
  font-weight: 600;
  color: #e74c3c;
  margin-bottom: var(--space-xs);
}

.UpgradeText {
  margin-bottom: var(--space-s);
  font-size: var(--f-s);
  color: var(--pc-dark);
}

.UpgradeBtn {
  display: inline-block;
  padding: var(--space-xs) var(--space-m);
}

.BillingActions {
  margin-top: var(--space-m);
  padding-top: var(--space-m);
  border-top: 1px solid var(--grey-90);
  text-align: left;
}

.ManageBillingBtn {
  background-color: white;
  color: var(--pc);
  border: 1px solid var(--pc);
  border-radius: var(--br-s);
  padding: var(--space-xs) var(--space-m);
  font-weight: 600;
  transition: all 0.2s ease;
}

.ManageBillingBtn:hover {
  background-color: var(--pc-lightest);
  transform: translateY(-2px);
}

.ManageSeatsBtn {
  font-size: var(--f-xs);
  background-color: var(--pc-lightest);
  color: var(--pc);
  border: 1px solid var(--pc);
  border-radius: var(--br-xs);
  padding: var(--space-xxs) var(--space-xs);
  font-weight: 600;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-block;
}

.ManageSeatsBtn:hover {
  background-color: var(--pc-light);
  color: var(--pc);
  text-decoration: none;
  transform: translateY(-1px);
}

.ProfileActions {
  margin-top: var(--space-m);
  display: flex;
  justify-content: flex-end;
}

.SettingsOption {
  padding: var(--space-xs) 0;
  border-bottom: 1px solid var(--grey-90);
}

.SettingsOption:last-child {
  border-bottom: none;
}

/* Admin section styles */
.AdminSection {
  padding: var(--space-s);
  background-color: rgba(255, 240, 240, 0.5);
  border-radius: var(--br-s);
  border: 1px dashed var(--delete-red);
}

.AdminSectionTitle {
  font-size: var(--f-s);
  font-weight: 600;
  color: var(--delete-red);
  margin-bottom: var(--space-xs);
}

.LicenseTestButtons {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--space-xs);
  margin-top: var(--space-s);
}

.LicenseTestBtn {
  padding: var(--space-xs);
  font-size: var(--f-xs);
  background-color: white;
  border: 1px solid var(--grey-70);
  border-radius: var(--br-s);
  transition: all 0.2s ease;
}

.LicenseTestBtn:hover {
  background-color: var(--grey-95);
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.AdminNote {
  color: var(--grey-50);
  font-style: italic;
  text-align: center;
}

/* User impersonation form styles */
.ImpersonateForm {
  background-color: white;
  border: 1px solid var(--grey-80);
  border-radius: var(--br-s);
  padding: var(--space-m);
  margin-top: var(--space-s);
}

.FormRow {
  margin-bottom: var(--space-s);
}

.FormRow:last-child {
  margin-bottom: 0;
}

.FormLabel {
  display: block;
  font-size: var(--f-xs);
  font-weight: 600;
  color: var(--grey-40);
  text-transform: uppercase;
  margin-bottom: var(--space-xxs);
}

.FormInput {
  width: 100%;
  padding: var(--space-xs);
  font-size: var(--f-s);
  border: 1px solid var(--grey-70);
  border-radius: var(--br-s);
  transition: border-color 0.2s ease;
}

.FormInput:focus {
  outline: none;
  border-color: var(--pc);
  box-shadow: 0 0 0 2px rgba(62, 85, 204, 0.1);
}

.FormInput:disabled {
  background-color: var(--grey-95);
  cursor: not-allowed;
}

.ImpersonateBtn {
  width: 100%;
  padding: var(--space-xs) var(--space-m);
  font-size: var(--f-s);
  font-weight: 600;
  background-color: var(--warning-orange);
  color: white;
  border: 1px solid var(--warning-orange);
  border-radius: var(--br-s);
  transition: all 0.2s ease;
  text-transform: uppercase;
}

.ImpersonateBtn:hover:not(:disabled) {
  background-color: var(--warning-orange-focus);
  border-color: var(--warning-orange-focus);
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.ImpersonateBtn:disabled {
  background-color: var(--grey-60);
  border-color: var(--grey-60);
  cursor: not-allowed;
  opacity: 0.7;
  transform: none;
  box-shadow: none;
}

.ErrorMessage {
  color: var(--delete-red);
  font-size: var(--f-s);
  padding: var(--space-xs);
  background-color: rgba(231, 4, 15, 0.1);
  border: 1px solid rgba(231, 4, 15, 0.3);
  border-radius: var(--br-s);
}
</style>
